import { useEffect, useRef, useState } from 'react';
import { Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getUserData } from 'redux/UserSlice';
import { fontSize, text } from 'styles/revampTokens';

import {
  addComment,
  approveTask,
  changeTaskDue,
  closeTask,
  getTask,
  getTaskData,
  listActivity,
  listComments,
  listTasks,
  maestroErrorMessage,
  reassignTask,
  reopenTask,
} from './maestroClient';
import { listModelPeople } from './modelPeople';
import { loadPlanningContext } from './planningContext';
import { PlanningPage, viewerFromUser } from './TasksChrome';
import TaskWorkspaceView, { shellFromTask } from './TaskWorkspace';
import { ActivityEvent, HubTask, TaskComment, TaskWorkspace, WorkspaceSpec } from './types';

const TaskPage = () => {
  const { taskId = '' } = useParams();
  const viewer = viewerFromUser(useSelector(getUserData));
  const [workspace, setWorkspace] = useState<TaskWorkspace | null>(null);
  const [preview, setPreview] = useState<HubTask | null>(null);
  const [siblings, setSiblings] = useState<HubTask[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [error, setError] = useState('');
  const [contextLine, setContextLine] = useState('');
  const heldSpec = useRef<WorkspaceSpec | null>(null);

  useEffect(() => {
    let cancelled = false;
    setComments([]);
    setActivity([]);
    setError('');

    // The inbox row is enough for the title. The plan itself can arrive after.
    listTasks()
      .then((tasks) => {
        if (cancelled) return;
        const current = tasks.find((task) => task.id === taskId) ?? null;
        setPreview(current);
        const runId = current?.process_run_id;
        setSiblings(runId ? tasks.filter((task) => task.process_run_id === runId) : []);
      })
      .catch(() => {});

    getTask(taskId)
      .then((next) => {
        if (cancelled) return;
        heldSpec.current = next.workspace_spec;
        setWorkspace(next);
        getTaskData(taskId)
          .then((data) => {
            if (cancelled || !data) return;
            setWorkspace((current) =>
              current && current.task.id === taskId
                ? { ...current, view: data.view, render: data.render }
                : current,
            );
          })
          .catch((loadError: unknown) => {
            if (!cancelled) {
              setError(maestroErrorMessage(loadError, 'The plan numbers could not be loaded.'));
            }
          });
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(maestroErrorMessage(loadError, 'That task could not be opened.'));
      });

    listComments(taskId)
      .then((thread) => {
        if (!cancelled) setComments(thread);
      })
      .catch(() => {});
    listActivity(taskId)
      .then((events) => {
        if (!cancelled) setActivity(events);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const current = workspace?.task.id === taskId ? workspace : null;
  const known =
    siblings.find((task) => task.id === taskId) ?? (preview?.id === taskId ? preview : null);
  const modelId = current?.task.model_id ?? known?.model_id;
  const scenarioId = current?.task.scenario_id ?? known?.scenario_id;

  useEffect(() => {
    if (!modelId || !scenarioId) return () => {};
    let cancelled = false;
    loadPlanningContext(modelId, scenarioId)
      .then((line) => {
        if (!cancelled) setContextLine(line);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [modelId, scenarioId]);

  if (error && !workspace) {
    return (
      <PlanningPage>
        <Text fontSize={fontSize.body} color={text.primary}>
          {error}
        </Text>
      </PlanningPage>
    );
  }
  const shell = current ? shellFromTask(current.task) : known ? shellFromTask(known) : null;
  const shownShell = shell ? { ...shell, context: contextLine } : null;

  return (
    <TaskWorkspaceView
      workspace={current}
      shell={shownShell}
      heldSpec={heldSpec.current}
      siblings={siblings}
      onWorkspace={(next) => {
        setWorkspace(next);
        if (next.view) return;
        getTaskData(next.task.id)
          .then((data) => {
            if (!data) return;
            setWorkspace((loaded) =>
              loaded && loaded.task.id === next.task.id
                ? { ...loaded, view: data.view, render: data.render }
                : loaded,
            );
          })
          .catch(() => {});
      }}
      viewer={viewer}
      comments={comments}
      activity={activity}
      onComment={async (body) => {
        try {
          const saved = await addComment(taskId, body);
          setComments((loaded) => [...loaded, saved]);
        } catch (actionError: unknown) {
          throw new Error(maestroErrorMessage(actionError, 'That comment could not be added.'));
        }
      }}
      onReassign={async (assigneeUserIds) => {
        try {
          await reassignTask(taskId, assigneeUserIds);
          const refreshed = await getTask(taskId);
          setWorkspace(refreshed);
          const data = await getTaskData(taskId);
          if (data) {
            setWorkspace((loaded) =>
              loaded && loaded.task.id === taskId
                ? { ...loaded, view: data.view, render: data.render }
                : loaded,
            );
          }
        } catch (actionError: unknown) {
          throw new Error(maestroErrorMessage(actionError, 'That task could not be reassigned.'));
        }
      }}
      onApprove={async (note) => {
        try {
          setWorkspace(await approveTask(taskId, note));
        } catch (actionError: unknown) {
          throw new Error(maestroErrorMessage(actionError, 'That task could not be approved.'));
        }
      }}
      onReopen={async (note) => {
        try {
          setWorkspace(await reopenTask(taskId, note));
        } catch (actionError: unknown) {
          throw new Error(maestroErrorMessage(actionError, 'That task could not be sent back.'));
        }
      }}
      onClose={async () => {
        try {
          setWorkspace(await closeTask(taskId));
        } catch (actionError: unknown) {
          throw new Error(maestroErrorMessage(actionError, 'That task could not be closed.'));
        }
      }}
      onChangeDue={async (dueAt) => {
        try {
          await changeTaskDue(taskId, dueAt);
          const refreshed = await getTask(taskId);
          setWorkspace(refreshed);
          const data = await getTaskData(taskId);
          if (data) {
            setWorkspace((loaded) =>
              loaded && loaded.task.id === taskId
                ? { ...loaded, view: data.view, render: data.render }
                : loaded,
            );
          }
        } catch (actionError: unknown) {
          throw new Error(maestroErrorMessage(actionError, 'That due date could not be saved.'));
        }
      }}
      loadModelPeople={async () => {
        const peopleModelId = current?.task.model_id;
        if (!peopleModelId) return [];
        try {
          return await listModelPeople(peopleModelId);
        } catch (actionError: unknown) {
          throw new Error(
            maestroErrorMessage(actionError, 'People on this model could not be loaded.'),
          );
        }
      }}
    />
  );
};

export default TaskPage;
